import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyP2Bold,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Check, ChevronDown, Download, FileText, Sparkles } from 'lucide-react';
import { useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

/** One filled field, as returned by the backend on `provider_metadata.data.fields`. */
export interface FilledField {
  name: string;
  label: string;
  value: string;
  page?: number;
}

export type FormFillCardStatus = 'generating' | 'completed';

export interface FormFillCardProps {
  status: FormFillCardStatus;
  title: string;
  fields: FilledField[];
  /** Opens the filled PDF in the artifact canvas. When omitted, the preview
   *  button falls back to opening `downloadUrl` in a new tab. */
  onPreview?: () => void;
  downloadUrl?: string;
}

const PREVIEW_FIELD_LIMIT = 6;

const SkeletonRow = ({ widthClass }: { widthClass: string }) => (
  <div className="flex items-center gap-3">
    <div className="h-3 w-24 shrink-0 animate-pulse rounded bg-surface-page" />
    <div className={cn('h-3 animate-pulse rounded bg-surface-page', widthClass)} />
  </div>
);

/**
 * Rich, animated card for PDF form fill tools (`fill_form_from_upload`,
 * `fill_pdf_form`). Shows progress while filling; on completion leads with
 * Preview / Download and an optional field summary.
 */
export const FormFillCard = ({
  status,
  title,
  fields,
  onPreview,
  downloadUrl,
}: FormFillCardProps) => {
  const intl = useIntl();
  const reduceMotion = useReducedMotion();
  const [fieldsExpanded, setFieldsExpanded] = useState(false);

  const previewFields = useMemo(
    () => fields.filter((field) => field.label && field.value),
    [fields]
  );
  const visibleFields = fieldsExpanded
    ? previewFields
    : previewFields.slice(0, PREVIEW_FIELD_LIMIT);
  const hiddenFieldCount = Math.max(0, previewFields.length - PREVIEW_FIELD_LIMIT);

  const handlePreview = () => {
    if (onPreview) {
      onPreview();
      return;
    }
    if (downloadUrl) window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  };

  if (status === 'generating') {
    return (
      <div
        className="flex w-full flex-col gap-3 rounded-xl border border-border bg-surface-accent p-4"
        data-testid="form-fill-card"
        data-status="generating"
      >
        <div className="flex items-center gap-3">
          <m.span
            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-page text-text-secondary"
            animate={reduceMotion ? undefined : { scale: [1, 1.08, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Sparkles className="size-4" />
          </m.span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <TypographyP2Bold className="truncate text-text-primary">
              {title}
            </TypographyP2Bold>
            <TypographyCaption className="text-text-tertiary">
              {intl.formatMessage({
                id: 'Fvza4xPG7i',
                defaultMessage: 'Mapping your answers into the form…',
              })}
            </TypographyCaption>
          </div>
        </div>
        <div className="flex flex-col gap-2.5 ps-11">
          <SkeletonRow widthClass="w-40" />
          <SkeletonRow widthClass="w-28" />
          <SkeletonRow widthClass="w-36" />
        </div>
      </div>
    );
  }

  const hasFields = previewFields.length > 0;

  return (
    <div
      className="flex w-full flex-col gap-3 rounded-xl border border-border bg-surface-accent p-4"
      data-testid="form-fill-card"
      data-status="completed"
    >
      <div className="flex items-center gap-3">
        <m.span
          className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-validation-success-muted text-validation-success"
          initial={reduceMotion ? false : { scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 24 }}
        >
          <Check className="size-4" />
        </m.span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <TypographyPBold className="truncate text-text-primary">
            {title}
          </TypographyPBold>
          <TypographyCaption className="text-text-tertiary">
            {hasFields
              ? intl.formatMessage(
                  {
                    id: 'KvFUIf5RT8',
                    defaultMessage:
                      '{count, plural, one {# field filled} other {# fields filled}}',
                  },
                  { count: previewFields.length }
                )
              : intl.formatMessage({
                  id: 'NPB5T0qfex',
                  defaultMessage: 'Form filled',
                })}
          </TypographyCaption>
        </div>
        <FileText className="size-4 shrink-0 text-text-tertiary" aria-hidden="true" />
      </div>

      {hasFields ? (
        <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-surface px-3 py-2 ps-11">
          <ul className="flex flex-col gap-1.5">
            {visibleFields.map((field) => (
              <li
                key={`${field.name}-${field.label}`}
                className="grid grid-cols-[minmax(0,38%)_minmax(0,1fr)] gap-2 text-xs"
              >
                <span className="truncate text-text-tertiary">{field.label}</span>
                <span className="truncate font-medium text-text-primary">
                  {field.value}
                </span>
              </li>
            ))}
          </ul>
          {hiddenFieldCount > 0 ? (
            <button
              type="button"
              onClick={() => setFieldsExpanded((prev) => !prev)}
              className="flex w-fit items-center gap-1 text-xs text-text-active hover:underline"
            >
              <ChevronDown
                className={cn(
                  'size-3.5 transition-transform',
                  fieldsExpanded && 'rotate-180'
                )}
                aria-hidden
              />
              {fieldsExpanded
                ? intl.formatMessage({
                    id: 'qyJtWyZ0yt',
                    defaultMessage: 'Show less',
                  })
                : intl.formatMessage(
                    {
                      id: 'Z184O6N3fq',
                      defaultMessage: 'Show {count} more',
                    },
                    { count: hiddenFieldCount }
                  )}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-center gap-2 ps-11">
        <Button size="sm" onClick={handlePreview} disabled={!downloadUrl && !onPreview}>
          <FileText className="me-1.5 size-4" />
          {intl.formatMessage({ id: 'Cxut/QTb5o', defaultMessage: 'Preview PDF' })}
        </Button>
        {downloadUrl ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(downloadUrl, '_blank', 'noopener,noreferrer')}
          >
            <Download className="me-1.5 size-4" />
            {intl.formatMessage({ id: '5q3qC0sC3r', defaultMessage: 'Download' })}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
